import { Badge, Box, Button, HStack, notify, Stack, Text } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { MdContentCopy } from "react-icons/md";
import { AutonomousAgent } from "services/autonomousAgentService";
import { getThemeColor } from "theme/colors";

interface WebhookPanelProps {
  agent: AutonomousAgent;
}

// The agent carries a relative webhookPath (/api/autonomous/hook/{companyId}/{agentId});
// the absolute URL is the API origin + that path.
const API_ORIGIN = process.env.REACT_APP_BASE_API_URL ?? "";

const boxStyles = {
  borderRadius: "md",
  bg: getThemeColor("codeBlock.bg"),
  borderWidth: "1px",
  borderColor: getThemeColor("codeBlock.border"),
  fontFamily: "var(--font-mono)",
  fontSize: "xs",
} as const;

interface SnippetProps {
  title: string;
  note?: string;
  code: string;
  copyLabel: string;
  onCopy: (code: string) => void;
}

/** One numbered step of the integration recipe: heading, optional note, curl block, copy action. */
const Snippet = ({ title, note, code, copyLabel, onCopy }: SnippetProps) => (
  <Stack gap={1}>
    <Text fontSize="xs" fontWeight="semibold">
      {title}
    </Text>
    {note && (
      <Text fontSize="xs" color={getThemeColor("text.secondary")}>
        {note}
      </Text>
    )}
    <Box
      as="pre"
      px={3}
      py={2}
      overflowX="auto"
      whiteSpace="pre"
      {...boxStyles}
    >
      {code}
    </Box>
    <HStack justify="flex-end">
      <Button intent="ghost" size="xs" onClick={() => onCopy(code)}>
        <MdContentCopy />
        {copyLabel}
      </Button>
    </HStack>
  </Stack>
);

/**
 * Read-only per-agent webhook reference: the absolute URL plus the integration recipe —
 * (1) a company admin signs in to get a bearer token (JWT), (2) with that JWT they generate a
 * per-user token (verifying the end-user's credential), (3) that per-user token authorizes the
 * webhook call, and (4) for asynchronous agents, the same token polls the run's status URL for
 * the output. Runs are attributed to the end-user the per-user token is bound to.
 */
const WebhookPanel = ({ agent }: WebhookPanelProps) => {
  const { t } = useTranslation();
  const isAsync = agent.executionMode !== "Synchronous";
  const absoluteUrl = `${API_ORIGIN}${agent.webhookPath}`;
  const curlExample = `curl -X POST "${absoluteUrl}" \\\n  -H "Authorization: Bearer <token>" \\\n  -H "Content-Type: application/json" \\\n  -d '{"message":"hello"}'`;

  // Step 1: the company admin signs in with their StrikeUp credentials to get a
  // bearer token (JWT) — that JWT authorizes the token generation in step 2.
  const loginUrl = `${API_ORIGIN}/api/auth/login`;
  const loginExample = `curl -X POST "${loginUrl}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"email":"<admin-email>","password":"<password>"}'`;

  // webhookPath is /api/autonomous/hook/{companyId}/{agentId} — the exchange
  // endpoint is company-scoped, so pre-fill the companyId for the integrator.
  const companyId = agent.webhookPath.split("/")[4] ?? "<companyId>";
  const exchangeUrl = `${API_ORIGIN}/api/autonomous/token`;
  const exchangeExample = `curl -X POST "${exchangeUrl}" \\\n  -H "Authorization: Bearer <admin-token>" \\\n  -H "Content-Type: application/json" \\\n  -d '{"companyId":"${companyId}","externalUserId":"<user>","credential":"<pin-or-password>"}'`;

  // Async only: the 202 hands back executionId + an absolute statusUrl (also in Location).
  const statusExample = `curl "${absoluteUrl}/runs/<executionId>" \\\n  -H "Authorization: Bearer <token>"`;

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify({
        type: "success",
        title: t("app.autonomousAgents.webhook.copied"),
      });
    } catch {
      notify({ type: "error", title: t("app.errors.requestFailed") });
    }
  };

  return (
    <Stack gap={3}>
      <HStack justify="space-between" align="center">
        <Text fontSize="sm" fontWeight="semibold">
          {t("app.autonomousAgents.webhook.title")}
        </Text>
        <Badge colorPalette="purple">
          {t(`app.autonomousAgents.mode.${isAsync ? "async" : "sync"}`)}
        </Badge>
      </HStack>

      <Stack gap={1}>
        <Text fontSize="xs" color={getThemeColor("text.secondary")}>
          {t("app.autonomousAgents.webhook.urlLabel")}
        </Text>
        <HStack gap={2}>
          <Box
            flex="1"
            px={3}
            py={2}
            overflowX="auto"
            whiteSpace="nowrap"
            {...boxStyles}
          >
            {absoluteUrl}
          </Box>
          <Button
            intent="secondary"
            size="sm"
            onClick={() => copy(absoluteUrl)}
          >
            <MdContentCopy />
            {t("app.autonomousAgents.webhook.copy")}
          </Button>
        </HStack>
      </Stack>

      <Text fontSize="xs" color={getThemeColor("text.secondary")}>
        {t("app.autonomousAgents.webhook.authNote")}
      </Text>

      {/* Step 1 — the company admin signs in to get a bearer token (JWT) for step 2. */}
      <Snippet
        title={t("app.autonomousAgents.webhook.loginTitle")}
        note={t("app.autonomousAgents.webhook.loginNote")}
        code={loginExample}
        copyLabel={t("app.autonomousAgents.webhook.copyLogin")}
        onCopy={copy}
      />

      {/* Step 2 — a company admin generates a per-user token (authenticated with the JWT from
          step 1, verifying the end-user's credential). It is bound to that user, so every
          run it triggers is attributed to them. */}
      <Snippet
        title={t("app.autonomousAgents.webhook.exchangeTitle")}
        note={t("app.autonomousAgents.webhook.exchangeNote")}
        code={exchangeExample}
        copyLabel={t("app.autonomousAgents.webhook.copyExchange")}
        onCopy={copy}
      />

      {/* Step 3 — call the webhook with the ahu_ token generated in step 2. */}
      <Snippet
        title={t("app.autonomousAgents.webhook.exampleTitle")}
        note={t("app.autonomousAgents.webhook.tokenFromStep1")}
        code={curlExample}
        copyLabel={t("app.autonomousAgents.webhook.copyCurl")}
        onCopy={copy}
      />
      {isAsync && (
        <Text fontSize="xs" color={getThemeColor("text.secondary")}>
          {t("app.autonomousAgents.webhook.asyncTriggerNote")}
        </Text>
      )}

      {/* Step 4 — async only: the run's output lives behind its status URL.
          Synchronous agents already return the output inline in step 3. */}
      {isAsync && (
        <>
          <Snippet
            title={t("app.autonomousAgents.webhook.statusTitle")}
            note={t("app.autonomousAgents.webhook.statusNote")}
            code={statusExample}
            copyLabel={t("app.autonomousAgents.webhook.copyStatus")}
            onCopy={copy}
          />
          <Text fontSize="xs" color={getThemeColor("text.secondary")}>
            {t("app.autonomousAgents.webhook.callbackNote")}
          </Text>
        </>
      )}
    </Stack>
  );
};

export default WebhookPanel;
